import React, { useEffect } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  Box,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import { ArrowForward } from "@mui/icons-material";
import Iconify from "../../../components/iconify";
import Request from "../../../utils/request";
import { toastify } from "../../../utils/toast";
import {
  loadingAction,
  loadingBtnAction,
  loginAction,
  setActiveStep,
  setSubscription,
  setEtsyShops,
} from "src/redux/actions/userActions";
import { persistCanvaOAuthState } from "src/pages/store/storeOAuth";
import { getAuthUserExtras } from "src/utils/apod";
import { useDispatch, useSelector } from "react-redux";
import {
  StyledLink,
  StyledLoadingButton,
} from "src/pages/auth/login/Login.styles";
import { setOAuthData } from "src/redux/actions/oAuthActions";
import { parseUrlParameters } from "src/pages/oAuth/utils";
import { setModalState } from "src/redux/actions/modalActions";
import TermsOfService from "src/sections/modals/user/TermsOfService";
import {
  getAllProducts,
  loadEtsyListingsByStatus,
} from "src/pages/products/request";
import { GoogleLogin } from "@react-oauth/google";
import { googleLogin, sendToSubscribe } from "src/pages/auth/register/requests";
import { prefetchDashboardRoutes } from "src/routes/prefetch";
import { jwtDecode } from "jwt-decode";
import {
  isVipsUserType,
  shouldRedirectToOrdersOnLogin,
  USER_TYPES,
} from "src/utils/userTypes";
import { preloadVipsDashboard } from "src/utils/preloadDashboard";
import { rememberAuthPortal } from "src/utils/authPortal";
import { getCatalogsList } from "src/pages/catalog/request";
import { takeSellerOrderDeepLinkPath } from "src/pages/orders/utils/orderDeepLink";

const schema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup
    .string()
    .required("Password is required")
    .min(8)
    .max(32)
    .required(),
});

const LoginForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const isLoading = useSelector((state) => state.user.loadingButton);

  const currentUrl = window.location.href;
  const urlObject = currentUrl.split("?");
  let getConsentURL =
    urlObject && urlObject?.length > 1 ? urlObject[1]?.split("=")[1] : "";
  const frompage = getConsentURL ? "/app/oauth2/auth?" + urlObject[2] : "";

  const urlParams = frompage ? parseUrlParameters(frompage) : [];
  const { redirect_uri, state, scope, client_id } = urlParams;
  const scopesArray = scope ? scope?.split(" ") : [];

  useEffect(() => {
    let data = {
      redirect_uri: redirect_uri,
      state: state,
      scopes: scopesArray,
      client_id: client_id,
    };

    dispatch(setOAuthData(data));
    localStorage.removeItem("userFormData");
  }, [client_id, dispatch, redirect_uri, scopesArray, state]);

  useEffect(() => {
    dispatch(loadingAction(false));
    dispatch(loadingBtnAction(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSuccess = async (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) {
      console.error("No credential returned");
      return;
    }

    // Decode to get user info
    const decoded = jwtDecode(idToken);

    // Extract email
    const email = decoded?.email;
    const name = decoded?.name;

    if (!email) {
      console.error("Email not found in token");
      return;
    }

    const data = {
      email: email,
      name: name,
    };

    prefetchDashboardRoutes();
    googleLogin(dispatch, navigate, data);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const login = async (data) => {
    // Warm dashboard chunks while the login request is in flight.
    prefetchDashboardRoutes();
    dispatch(loadingBtnAction(true));
    try {
      const res = await Request.post("/login", data);

      var response = res["data"];
      var data_filter = response["data"];
      const userData = data_filter.user || data_filter;
      const token = data_filter["token"];
      const oauth = data_filter["oauth"];
      const expiresIn = data_filter["expiresIn"];
      const subscription = data_filter["subscription"];
      const etsy_shops =
        userData["etsy_shops"] || data_filter["etsy_shops"] || [];
      const etsyStoreConnected = etsy_shops.length > 0 ? 1 : 0;
      const image = userData.image ?? "";

      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem("oAuth", oauth);
        localStorage.setItem("expiresIn", expiresIn);
        localStorage.setItem("etsyStore", etsyStoreConnected);
        localStorage.setItem("registered", false);
        persistCanvaOAuthState({ ...data_filter, user: userData });

        dispatch(
          loginAction(
            token,
            userData.id,
            userData.name,
            userData.email,
            image,
            userData.UserType,
            getAuthUserExtras(userData, data_filter),
          ),
        );
        dispatch(setSubscription(subscription));
        if (etsy_shops && etsy_shops.length > 0) {
          dispatch(setEtsyShops(etsy_shops));
        }

        if (userData.UserType === USER_TYPES.SELLER) {
          getAllProducts(dispatch);
          loadEtsyListingsByStatus(dispatch, "active");
        }

        rememberAuthPortal(userData.UserType);

        if (isVipsUserType(userData.UserType)) {
          await preloadVipsDashboard();
          getCatalogsList(dispatch);
        }

        const sellerOrderPath = frompage
          ? ""
          : takeSellerOrderDeepLinkPath(userData.UserType);

        if (frompage) {
          navigate(frompage);
        } else if (sellerOrderPath) {
          navigate(sellerOrderPath);
        } else if (userData.UserType === USER_TYPES.ADMIN_ACCESS_GUARD) {
          navigate("/dashboard/user");
        } else if (shouldRedirectToOrdersOnLogin(userData.UserType)) {
          navigate("/dashboard/orders");
        } else if (userData.UserType === "Whitney") {
          navigate("/dashboard/mockup-library");
        } else {
          navigate("/dashboard/app");
        }
        dispatch(loadingBtnAction(false));
      }
    } catch (e) {
      dispatch(loadingBtnAction(false));
      const status = e?.response?.status;

      if (status === 451) {
        dispatch(setModalState(<TermsOfService email={data?.email} />));
        return;
      }

      if (status === 402) {
        sendToSubscribe(navigate, e?.response?.data?.data?.token);
        return;
      }

      if (e?.isSessionExpired) return;

      const rawMessage = String(e?.response?.data?.message || "").trim();
      const normalized = rawMessage.toLowerCase();
      const isUnauthenticated =
        status === 401 ||
        normalized === "unauthenticated." ||
        normalized === "unauthenticated" ||
        normalized === "unathenticated." ||
        normalized === "unathenticated";

      const message = isUnauthenticated
        ? "Invalid email or password. Please try again."
        : rawMessage || e?.message || "Login failed. Please try again.";
      toastify("error", message);
    }
  };

  const onSubmitHandler = (data) => {
    reset();
    login(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)}>
      <Typography variant="h4" color={"primary.main"} gutterBottom mb={2}>
        Login to Your Account
      </Typography>
      <Stack spacing={3}>
        <TextField
          {...register("email")}
          type="email"
          label="Email"
          autoComplete="email"
          required
          sx={{
            marginTop: "0px",
            styleOverrides: {
              "&.MuiListItem-root": {
                marginTop: "0px",
              },
            },
          }}
        />
        {errors.email?.message && (
          <Alert severity="error">{errors.email?.message}</Alert>
        )}

        <TextField
          {...register("password")}
          type={showPassword ? "text" : "password"}
          label="Password"
          autoComplete="current-password"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
            maxLength: 25,
            minLength: 8,
          }}
        />
        {errors.password?.message && (
          <Alert severity="error">{errors.password?.message}</Alert>
        )}
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ my: 2 }}
      >
        <StyledLink
          variant="subtitle2"
          underline="hover"
          to={""}
          onClick={() => dispatch(setModalState(<TermsOfService email={""} />))}
        >
          Accept Terms Of Service
        </StyledLink>
        <StyledLink
          variant="subtitle2"
          underline="hover"
          to={"/reset-password"}
          onClick={() => dispatch(setActiveStep(0))}
        >
          Forgot password?
        </StyledLink>
      </Stack>

      {/* <Box sx={{ my: 2 }}></Box> */}
      <StyledLoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isLoading}
        loadingIndicator={<CircularProgress color="inherit" size={16} />}
      >
        Login
      </StyledLoadingButton>

      <Divider sx={{ my: 3 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          OR
        </Typography>
      </Divider>

      <GoogleLogin
        onSuccess={handleSuccess}
        onError={() => {
          console.error("Google Login Failed");
          toastify(
            "error",
            "Google Sign-In failed. Please check that your origin is authorized in Google Cloud Console.",
          );
        }}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems="center"
        justifyContent="center"
        spacing={{ xs: 0.5, sm: 0.75 }}
        sx={{ mt: 2, mb: 5 }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Don&apos;t have an account?
        </Typography>
        <Box
          component={NavLink}
          to="/signup"
          sx={{
            color: "primary.main",
            fontWeight: 600,
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.25,
            fontSize: "0.875rem",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Sign Up
          <ArrowForward sx={{ fontSize: 16 }} />
        </Box>
      </Stack>
    </form>
  );
};

export default LoginForm;
